import { useEffect, useMemo, useState } from "react";
import { useStatusReference, themeForStatus } from "@/lib/statusTheme";
import { api } from "@/lib/api";
import { sortRecentTicketsDesc } from "@/lib/ticketSort";
import { computeThisMonthRange } from "@/lib/exportDates";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Ticket as TicketIcon, CheckCircle2, Clock, Wallet, CalendarRange, CalendarClock, ChevronRight, XCircle, Inbox, Fingerprint, MapPin, Wrench, ArrowRight, UserCheck, UserCog, LogIn, Timer, Search } from "lucide-react";
import { formatWIB, formatWIBDate } from "@/lib/time";
import TechnicianStats from "@/components/TechnicianStats";
import SplashLoader from "@/components/SplashLoader";

function formatIDR(n) {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);
}

function MetricCard({ label, value, sub, icon: Icon, accent, testid, onClick, actions }) {
  const clickable = typeof onClick === "function";
  const handleKey = (e) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testid}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? handleKey : undefined}
      aria-label={clickable ? `Buka daftar ${label}` : undefined}
      className={`group relative bg-white border border-[#E5E7EB] rounded-xl p-3 sm:p-3.5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] flex flex-col transition-[transform,box-shadow,border-color] duration-200 ease-out will-change-transform ${
        clickable
          ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-[#D1D5DB] active:scale-[0.985] active:shadow-sm active:transition-transform active:duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
          : "hover:-translate-y-px hover:shadow-sm"
      }`}
    >
      <div className="flex items-center gap-2">
        <div
          data-testid={`${testid}-icon`}
          className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-[transform,box-shadow] duration-200 ease-out ${
            clickable
              ? "group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:scale-110 group-hover:shadow-[0_6px_12px_-4px_rgba(0,0,0,0.18)] group-active:scale-105"
              : ""
          }`}
          style={{ background: accent + "14", color: accent }}
        >
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#6B7280] leading-tight line-clamp-2 min-w-0">
          {label}
        </div>
      </div>
      <div className="mt-2 font-display text-[28px] leading-none font-black tracking-tighter font-mono-tab" style={{ color: "#0A0A0A" }}>{value}</div>
      {sub && (
        <div className="mt-1.5 flex items-end justify-between gap-2 text-[10px] sm:text-[11px] leading-snug text-[#4B5563]">
          <div className="min-w-0 flex-1">{sub}</div>
          {clickable && !actions && (
            <ChevronRight
              data-testid={`${testid}-chevron`}
              className="lg:hidden h-3.5 w-3.5 text-[#9CA3AF] flex-shrink-0 -mb-0.5 transition-transform duration-200 group-active:translate-x-0.5"
              aria-hidden
            />
          )}
        </div>
      )}
      {actions && (
        <div
          className="mt-auto pt-2 flex flex-col items-stretch gap-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      )}
    </motion.div>
  );
}

const ATT_CHIP_ITEMS = [
  { key: "H", sfx: "h", status: "HADIR", label: "Hadir", color: "#16A34A", Icon: UserCheck },
  { key: "TL", sfx: "tl", status: "TERLAMBAT", label: "Terlambat", color: "#D97706", Icon: Clock, solidWhenPositive: true },
  { key: "BC", sfx: "bc", status: "BELUM_CHECKIN", label: "Belum Check-in", color: "#475569", Icon: LogIn },
  { key: "A", sfx: "a", status: "ALPA", label: "ALPA", color: "#E11D48", Icon: XCircle, solidWhenPositive: true },
];

// Ringkasan absensi hari ini per kelompok. Label → /operations, badge → Rekap Absensi (role + status).
function AttendanceChip({ testid, idPrefix, label, Icon, kpi, role, date, cutoff, navigate }) {
  return (
    <div
      className="min-h-8 py-1 px-0.5 rounded-lg border border-[#E5E7EB] bg-white inline-flex flex-wrap items-center gap-x-1 gap-y-1 max-w-full"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => navigate("/operations")}
        className="h-6 px-1 rounded-md inline-flex items-center gap-1 text-[10.5px] 2xl:text-[11px] font-bold text-[#0A0A0A] whitespace-nowrap hover:bg-[#F3F4F6] transition-colors touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
        data-testid={`${testid}-label`}
        title="Monitoring Absensi & Lembur"
      >
        <Icon className="h-3.5 w-3.5 text-[#6B7280] sm:hidden 2xl:block" /> {label}
      </button>
      <span className="inline-flex flex-wrap items-center gap-0.5">
        {ATT_CHIP_ITEMS.map(({ key, sfx, status, label: l, color, Icon: I, solidWhenPositive }) => {
          const val = kpi[key] ?? 0;
          const solid = solidWhenPositive && val > 0;
          return (
            <button
              key={key}
              type="button"
              onClick={() => navigate(`/attendance-report?start_date=${date}&end_date=${date}&role=${role}&status=${status}`)}
              disabled={!date}
              className={`h-6 inline-flex items-center gap-0.5 rounded-md px-1 text-[10px] 2xl:text-[11px] font-semibold whitespace-nowrap transition-[transform,box-shadow] hover:-translate-y-px hover:shadow-sm touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] disabled:cursor-default ${solid ? "text-white" : ""}`}
              style={solid ? { background: color, boxShadow: `0 2px 6px -2px ${color}99` } : { background: color + "14", color }}
              data-testid={`${idPrefix}-${sfx}-btn`}
              aria-label={`Rekap ${l} ${label.toLowerCase()} hari ini, total ${val}`}
              title={`Lihat Rekap Absensi: ${l} ${label.toLowerCase()} hari ini${status === "TERLAMBAT" && cutoff ? ` (check-in ≥ ${cutoff} WIB)` : ""}`}
            >
              <I className="h-3 w-3" />
              <span>{l}</span>
              <span className="font-mono-tab font-black" data-testid={`${idPrefix}-${sfx}`}>{val}</span>
            </button>
          );
        })}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { user, hasPriv } = useAuth();
  const navigate = useNavigate();
  const statusMaster = useStatusReference();
  // Every role (teknisi included) sees GLOBAL by default and may opt-in to "own only".
  const canToggleOwn = !!user;
  const onlyMineStorageKey = user?.id ? `aroma_dashboard_only_mine_${user.id}` : null;
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [dateRange, setDateRange] = useState({ date_from: "", date_to: "" });
  const [onlyMine, setOnlyMine] = useState(() => {
    if (typeof window === "undefined" || !onlyMineStorageKey) return false;
    return localStorage.getItem(onlyMineStorageKey) === "1";
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [wilayahAllOpen, setWilayahAllOpen] = useState(false);
  // Attendance Live KPI — chip di sebelah tombol Absensi. Fetched only for
  // roles with attendance_recap_view (admin/koord). Reuses existing
  // `/operations/dashboard` endpoint — no new backend.
  const [attendanceKpi, setAttendanceKpi] = useState(null);
  const [lateSchedule, setLateSchedule] = useState(null);

  const load = async (range = dateRange, mine = onlyMine) => {
    try {
      const params = {};
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      if (mine && canToggleOwn) params.only_mine = 1;
      const listParams = { ...params };
      if (mine && canToggleOwn) {
        if (user?.role === "teknisi") {
          // Default backend scope for teknisi = PIC OR tim_pendamping. No extra filter.
        } else if (user?.name) {
          // koordinator/admin: narrow to their PIC assignments.
          listParams.pic = user.name;
        }
      } else if (user?.role === "teknisi") {
        // Default (global) view — bypass the teknisi PIC scoping on /tickets so
        // field staff can see the full pool queue and the map.
        listParams.scope = "all";
      }
      const [s, t] = await Promise.all([
        api.get("/tickets/stats", { params }),
        // Fetch a wider window than we display so the client-side re-sort by
        // `tgl_masuk` (with `created_at` fallback for legacy rows) can pick
        // the truly-newest 8 tickets even when the backend default order
        // (`created_at desc`) diverges from `tgl_masuk` for older imports.
        api.get("/tickets", { params: { ...listParams, limit: 50 } }),
      ]);
      setStats(s.data);
      setRecent(sortRecentTicketsDesc(t.data).slice(0, 8));
    } catch (err) {
      console.error("[Dashboard] failed to load stats/tickets:", err);
    }
    finally { setInitialLoading(false); }
  };

  useEffect(() => {
    // First-load default period = first day of current month → today (WIB, from server).
    // Timezone MUST come from the app system (Asia/Jakarta), not the browser clock.
    let cancelled = false;
    (async () => {
      let dateFrom = "";
      let dateTo = "";
      try {
        const { data } = await api.get("/system/time");
        // display_time is "YYYY-MM-DD HH:MM:SS" in WIB (Asia/Jakarta).
        const wibDate = (data?.display_time || "").slice(0, 10); // "YYYY-MM-DD"
        if (/^\d{4}-\d{2}-\d{2}$/.test(wibDate)) {
          dateTo = wibDate;
          dateFrom = `${wibDate.slice(0, 7)}-01`;
        }
      } catch (err) {
        console.error("[Dashboard] failed to fetch server time, loading without default range:", err);
      }
      if (cancelled) return;
      const range = { date_from: dateFrom, date_to: dateTo };
      setDateRange(range);
      load(range, onlyMine);
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, []);

  // Auto-refresh Dashboard every 30s while page is mounted. Preserves current
  // filter (dateRange + onlyMine) — no full page reload. Timer resets when
  // filters change and is cleaned up on unmount.
  useEffect(() => {
    const id = setInterval(() => { load(dateRange, onlyMine); }, 30000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [dateRange.date_from, dateRange.date_to, onlyMine]);

  // Attendance Live KPI — poll /operations/dashboard for HADIR/TERLAMBAT/ALPA
  // ringkasan hari ini. Only for roles with attendance_recap_view (admin+
  // koord). Silently fail-open: chip hidden on 403/network error.
  useEffect(() => {
    if (!hasPriv?.("attendance_recap_view")) return;
    let cancelled = false;
    const fetchKpi = async () => {
      try {
        const { data } = await api.get("/operations/dashboard");
        if (!cancelled) {
          const a = data?.attendance || {};
          const ak = data?.attendance_koord || {};
          setAttendanceKpi({
            H: a.HADIR ?? 0,
            TL: a.TERLAMBAT ?? 0,
            BC: a.BELUM_CHECKIN ?? 0,
            A: a.ALPA ?? 0,
            koord: {
              H: ak.HADIR ?? 0,
              TL: ak.TERLAMBAT ?? 0,
              BC: ak.BELUM_CHECKIN ?? 0,
              A: ak.ALPA ?? 0,
            },
            koord_cohort_size: data?.koord_cohort_size ?? 0,
            date: data?.date,
            ovInProgress: data?.overtime?.IN_PROGRESS ?? 0,
          });
        }
      } catch {
        if (!cancelled) setAttendanceKpi(null);
      }
    };
    fetchKpi();
    // Jadwal batas terlambat global (read-only) untuk label badge Terlambat.
    api.get("/settings/attendance")
      .then(({ data }) => { if (!cancelled) setLateSchedule(data?.late_schedule || null); })
      .catch(() => {});
    const id = setInterval(fetchKpi, 60000);
    return () => { cancelled = true; clearInterval(id); };
    /* eslint-disable-next-line */
  }, [user?.id]);

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days + 1);
    const r = { date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) };
    setDateRange(r);
    load(r, onlyMine);
  };

  const clearRange = () => {
    setDateRange({ date_from: "", date_to: "" });
    load({ date_from: "", date_to: "" }, onlyMine);
  };

  const toggleOnlyMine = () => {
    const next = !onlyMine;
    setOnlyMine(next);
    if (onlyMineStorageKey) {
      try { localStorage.setItem(onlyMineStorageKey, next ? "1" : "0"); } catch (_) {}
    }
    load(dateRange, next);
  };

  const bs = stats?.by_status || {};
  // Weekday dari tanggal server (WIB) → key jadwal, index sama dengan JS getUTCDay().
  const todayCutoff = (() => {
    if (!lateSchedule || !attendanceKpi?.date) return null;
    const keys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const d = new Date(`${attendanceKpi.date}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? null : lateSchedule[keys[d.getUTCDay()]] || null;
  })();

  if (initialLoading) {
    return <SplashLoader label="Memuat dashboard..." />;
  }

  return (
    <div className="space-y-4" data-testid="dashboard">
      {/* Toolbar: cakupan, periode, absensi, tugas saya */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl px-3 sm:px-4 py-2.5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] space-y-2" data-testid="dashboard-toolbar">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-[260px]" data-testid="dashboard-title-block">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] truncate">
            Selamat datang, {user?.name}
          </div>
          <h1 className="font-display text-xl sm:text-2xl font-black tracking-tighter leading-tight">
            Ringkasan Operasional
          </h1>
          <p className="text-[11px] sm:text-xs text-[#6B7280] truncate" title="Pantau aktivitas tiket, pekerjaan teknisi, dan status operasional tim Anda dalam satu halaman.">
            Pantau aktivitas tiket, pekerjaan teknisi, dan status operasional tim Anda dalam satu halaman.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-date-range">
          {stats?.scope === "own" && (
            <span
              data-testid="scope-badge-own"
              className="h-8 inline-flex items-center gap-1.5 bg-[#EA580C]/10 text-[#EA580C] text-[10px] font-bold uppercase tracking-widest px-3 rounded-lg border border-[#EA580C]/20"
              title="Data yang ditampilkan hanya milik Anda"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" /> Data Milik Anda
            </span>
          )}
          {stats?.scope === "global" && (
            <span
              data-testid="scope-badge-global"
              className="h-8 inline-flex items-center gap-1.5 bg-[#0055FF]/10 text-[#0055FF] text-[10px] font-bold uppercase tracking-widest px-3 rounded-lg border border-[#0055FF]/20"
              title="Statistik seluruh tim"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#0055FF]" /> Global — Semua Tim
            </span>
          )}
          <div className="flex items-center gap-1 sm:gap-1.5 bg-white border border-[#E5E7EB] rounded-lg px-2 sm:px-2.5 h-8 min-w-0 w-full sm:w-auto" data-testid="dash-date-range-box">
            <CalendarRange className="h-3.5 w-3.5 text-[#9CA3AF] flex-shrink-0" />
            <input
              type="date"
              value={dateRange.date_from}
              onChange={(e) => { const r = { ...dateRange, date_from: e.target.value }; setDateRange(r); load(r, onlyMine); }}
              className="text-xs outline-none bg-transparent flex-1 w-0 min-w-0 sm:flex-none sm:w-[7.5rem]"
              data-testid="dash-date-from"
            />
            <span className="text-[#9CA3AF] text-xs">—</span>
            <input
              type="date"
              value={dateRange.date_to}
              onChange={(e) => { const r = { ...dateRange, date_to: e.target.value }; setDateRange(r); load(r, onlyMine); }}
              className="text-xs outline-none bg-transparent flex-1 w-0 min-w-0 sm:flex-none sm:w-[7.5rem]"
              data-testid="dash-date-to"
            />
          </div>
          <div className="inline-flex items-center gap-1 rounded-lg bg-[#F3F4F6] p-0.5">
            <button onClick={() => applyPreset(7)} className="h-8 px-3 rounded-md text-xs font-semibold text-[#374151] hover:bg-white hover:shadow-sm transition-[background-color,box-shadow] touch-manipulation" data-testid="preset-7d">7 Hari</button>
            <button onClick={() => applyPreset(30)} className="h-8 px-3 rounded-md text-xs font-semibold text-[#374151] hover:bg-white hover:shadow-sm transition-[background-color,box-shadow] touch-manipulation" data-testid="preset-30d">30 Hari</button>
            {(dateRange.date_from || dateRange.date_to) && (
              <button onClick={clearRange} className="h-8 px-3 rounded-md text-xs font-semibold text-[#374151] hover:bg-white hover:shadow-sm transition-[background-color,box-shadow] touch-manipulation" data-testid="btn-clear-range">
                Semua
              </button>
            )}
          </div>
        </div>
        </div>
        {(hasPriv?.("attendance_recap_view") || canToggleOwn) && (
          <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-[#F3F4F6]" data-testid="dashboard-toolbar-secondary">
            {hasPriv?.("attendance_recap_view") && (
              <button
                type="button"
                onClick={() => navigate("/operations")}
                className="h-8 px-2.5 rounded-lg text-xs font-semibold border transition-colors touch-manipulation inline-flex items-center gap-1.5 cursor-pointer border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-[#F8F9FA] hover:text-[#0A0A0A]"
                data-testid="dashboard-absensi-shortcut"
                title="Monitoring Absensi & Lembur"
                aria-label="Absensi"
              >
                <Fingerprint className="h-3.5 w-3.5" />
                <span className="hidden 2xl:inline">Absensi</span>
              </button>
            )}
            {hasPriv?.("attendance_recap_view") && attendanceKpi && (
              <AttendanceChip
                testid="dashboard-absensi-kpi-chip"
                idPrefix="attendance-kpi"
                label="Teknisi"
                Icon={Wrench}
                kpi={attendanceKpi}
                role="teknisi"
                date={attendanceKpi.date}
                cutoff={todayCutoff}
                navigate={navigate}
              />
            )}
            {hasPriv?.("attendance_recap_view") && attendanceKpi?.koord && attendanceKpi.koord_cohort_size > 0 && (
              <AttendanceChip
                testid="dashboard-absensi-koord-kpi-chip"
                idPrefix="attendance-koord-kpi"
                label="Koordinator"
                Icon={UserCog}
                kpi={attendanceKpi.koord}
                role="koordinator"
                date={attendanceKpi.date}
                cutoff={todayCutoff}
                navigate={navigate}
              />
            )}
            {hasPriv?.("attendance_recap_view") && attendanceKpi && (() => {
              const n = attendanceKpi.ovInProgress;
              return (
                <button
                  type="button"
                  onClick={() => navigate("/operations")}
                  className={`h-8 px-1.5 rounded-lg border text-[10px] 2xl:text-[11px] font-semibold inline-flex items-center gap-1 whitespace-nowrap transition-[transform,box-shadow] hover:-translate-y-px hover:shadow-sm touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] ${n > 0 ? "bg-[#0055FF] border-[#0055FF] text-white shadow-[0_2px_6px_-2px_rgba(0,85,255,0.6)]" : "bg-[#0055FF]/[0.08] border-[#0055FF]/20 text-[#0055FF]"}`}
                  title={`Lembur Berlangsung ${n}`}
                  aria-label={`Lembur Berlangsung ${n}, buka Absensi & Lembur`}
                  data-testid="dashboard-overtime-chip"
                >
                  <Timer className="h-3.5 w-3.5" />
                  <span className="2xl:hidden">Lembur</span>
                  <span className="hidden 2xl:inline">Lembur Berlangsung</span>
                  <span className="font-mono-tab font-black" data-testid="dashboard-overtime-count">{n}</span>
                </button>
              );
            })()}
            {canToggleOwn && (
              <label
                className={`h-8 px-2 rounded-lg text-xs font-semibold border transition-colors touch-manipulation inline-flex items-center gap-1.5 cursor-pointer ${onlyMine ? "border-[#EA580C] bg-[#EA580C]/10 text-[#EA580C]" : "border-[#E5E7EB] bg-white text-[#4B5563] hover:bg-[#F8F9FA]"}`}
                data-testid="toggle-only-mine"
              >
                <input
                  type="checkbox"
                  checked={onlyMine}
                  onChange={toggleOnlyMine}
                  className="h-3.5 w-3.5 accent-[#EA580C]"
                  data-testid="chk-only-mine"
                />
                <span className="2xl:hidden">Tugas Saya</span>
                <span className="hidden 2xl:inline">Tampilkan Tugas Saya Saja</span>
              </label>
            )}
          </div>
        )}
      </div>

      {(() => {
        // Fase 5.9 — Ringkasan Operasional final structure: 6 kelompok utama.
        // Bucketing (mutually exclusive, backend by_status is source of truth):
        //   1. Total Operasional  = DISTINCT tickets in period (stats.total)
        //   2. Selesai            = DONE                (completed_at event)
        //   3. Dijadwalkan        = SCHEDULED + RESCHEDULE (two SEPARATE
        //      quick-actions within the same card so users pick either
        //      "first-time scheduled" or "moved schedule" independently)
        //   4. Sedang Berjalan    = SURVEY + PROSES + WAITING
        //      (three SEPARATE quick-actions; SURVEY moved here from OPEN
        //      per Fase 5.9 spec — it IS active field work, not intake)
        //   5. OPEN               = OPEN only            (renamed from "Baru";
        //      Claim button still routes to /pool)
        //   6. Dibatalkan         = CANCEL               (cancelled_at event)
        // stats.total remains the Fase 5.8 distinct union — trust the backend.
        const selesai      = bs.DONE ?? 0;
        const scheduled    = bs.SCHEDULED ?? 0;
        const reschedule   = bs.RESCHEDULE ?? 0;
        const dijadwalkan  = scheduled + reschedule;
        const survey       = bs.SURVEY ?? 0;
        const proses       = bs.PROSES ?? 0;
        const waiting      = bs.WAITING ?? 0;
        const berjalan     = survey + proses + waiting;
        // Fase 5.9.9 — OPEN card = tiket yang bisa diklaim (belum ada PIC),
        // SSoT dari `stats.open_claimable` (mirror of /tickets/pool query).
        // Bukan `bs.OPEN` yang menghitung SEMUA current-status OPEN termasuk
        // yang sudah punya PIC.
        const openCount    = stats?.open_claimable ?? 0;
        const dibatalkan   = bs.CANCEL ?? 0;
        const totalTiket   = stats?.total ?? (selesai + dijadwalkan + berjalan + openCount);
        // Propagate the active period to every drill-down so the tiket list
        // matches the counter (Fase 5.8 occurrence semantics honour date on
        // the SSoT event field per status automatically on the backend).
        const dfp = new URLSearchParams();
        if (dateRange.date_from) dfp.set("date_from", dateRange.date_from);
        if (dateRange.date_to) dfp.set("date_to", dateRange.date_to);
        const withStatus = (s) => {
          const p = new URLSearchParams(dfp);
          if (s) p.set("status", s);
          const qs = p.toString();
          return `/tickets${qs ? "?" + qs : ""}`;
        };
        // Fase 5.9.2 — Sub-copy words themselves ARE the links. The block
        // rows below the card have been removed entirely; instead, the words
        // "scheduled", "reschedule", "survey", "proses" and "tunggu" inside
        // the descriptive sub text (e.g. "10 scheduled · 0 reschedule") are
        // rendered as inline <button> anchors that carry the same
        // data-testid contract as before. Styled minimally: hover:underline
        // + slight text-color darkening + cursor-pointer. Text size stays
        // 10-12px consistent with the surrounding sub copy.
        const inlineLink =
          "font-semibold cursor-pointer hover:underline underline-offset-2 decoration-2 focus:outline-none focus-visible:underline transition-colors touch-manipulation";
        const wordLink = (variant) => {
          const styles = {
            indigo: `${inlineLink} text-[#3730A3] hover:text-[#1E1B4B]`,
            amber:  `${inlineLink} text-[#92400E] hover:text-[#451A03]`,
            cyan:   `${inlineLink} text-[#155E75] hover:text-[#083344]`,
            blue:   `${inlineLink} text-[#1E40AF] hover:text-[#1E3A8A]`,
            gray:   `${inlineLink} text-[#374151] hover:text-[#111827]`,
          };
          return styles[variant] || styles.gray;
        };
        return (
          <div
            className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
            data-testid="dashboard-metrics"
          >
            <MetricCard
              label="Total Operasional"
              value={totalTiket}
              sub={`Rp ${Number(stats?.total_revenue || 0).toLocaleString("id-ID")} · Pendapatan DONE`}
              icon={TicketIcon}
              accent="#0055FF"
              testid="metric-total"
              onClick={() => navigate(withStatus(""))}
            />
            <MetricCard
              label="Selesai"
              value={selesai}
              sub="Tiket DONE dalam periode"
              icon={CheckCircle2}
              accent="#065F46"
              testid="metric-selesai"
              onClick={() => navigate(withStatus("DONE"))}
            />
            <MetricCard
              label="Dijadwalkan"
              value={dijadwalkan}
              sub={
                <div className="flex flex-wrap gap-x-1.5 gap-y-0.5">
                  <div className="whitespace-nowrap py-1 sm:py-0">
                    {scheduled}{" "}
                    <button
                      type="button"
                      data-testid="metric-dijadwalkan-action-scheduled"
                      onClick={() => navigate(withStatus("SCHEDULED"))}
                      className={wordLink("indigo")}
                      aria-label="Lihat tiket SCHEDULED"
                    >
                      scheduled
                    </button>
                  </div>
                  <span className="text-[#D1D5DB]" aria-hidden>·</span>
                  <div className="whitespace-nowrap py-1 sm:py-0">
                    {reschedule}{" "}
                    <button
                      type="button"
                      data-testid="metric-dijadwalkan-action-reschedule"
                      onClick={() => navigate(withStatus("RESCHEDULE"))}
                      className={wordLink("amber")}
                      aria-label="Lihat tiket RESCHEDULE"
                    >
                      reschedule
                    </button>
                  </div>
                </div>
              }
              icon={CalendarClock}
              accent="#3730A3"
              testid="metric-dijadwalkan"
            />
            <MetricCard
              label="Sedang Berjalan"
              value={berjalan}
              sub={
                <div className="flex flex-wrap gap-x-1.5 gap-y-0.5">
                  <div className="whitespace-nowrap py-1 sm:py-0">
                    {survey}{" "}
                    <button
                      type="button"
                      data-testid="metric-berjalan-action-survey"
                      onClick={() => navigate(withStatus("SURVEY"))}
                      className={wordLink("cyan")}
                      aria-label="Lihat tiket SURVEY"
                    >
                      survey
                    </button>
                  </div>
                  <span className="text-[#D1D5DB]" aria-hidden>·</span>
                  <div className="whitespace-nowrap py-1 sm:py-0">
                    {proses}{" "}
                    <button
                      type="button"
                      data-testid="metric-berjalan-action-proses"
                      onClick={() => navigate(withStatus("PROSES"))}
                      className={wordLink("blue")}
                      aria-label="Lihat tiket PROSES"
                    >
                      proses
                    </button>
                  </div>
                  <span className="text-[#D1D5DB]" aria-hidden>·</span>
                  <div className="whitespace-nowrap py-1 sm:py-0">
                    {waiting}{" "}
                    <button
                      type="button"
                      data-testid="metric-berjalan-action-waiting"
                      onClick={() => navigate(withStatus("WAITING"))}
                      className={wordLink("gray")}
                      aria-label="Lihat tiket WAITING"
                    >
                      tunggu
                    </button>
                  </div>
                </div>
              }
              icon={Clock}
              accent="#1E40AF"
              testid="metric-berjalan"
            />
            <MetricCard
              label="Open"
              value={openCount}
              sub="Siap diklaim (belum ada PIC)"
              icon={Inbox}
              accent="#EA580C"
              testid="metric-open"
              actions={
                <button
                  type="button"
                  data-testid="metric-open-action-klaim"
                  onClick={() => navigate("/pool")}
                  className="inline-flex items-center justify-between gap-2 px-3 min-h-[44px] py-3 sm:py-0 sm:h-8 sm:min-h-0 rounded-md text-[11px] font-bold uppercase tracking-wider transition focus:outline-none focus-visible:ring-2 w-full min-w-0 bg-[#EA580C] border border-[#EA580C] text-white hover:bg-[#C2410C] hover:border-[#C2410C] focus-visible:ring-[#EA580C]/40 touch-manipulation"
                  aria-label="Buka pool klaim tiket"
                >
                  <span className="truncate">Klaim</span>
                  <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                </button>
              }
            />
            <MetricCard
              label="Dibatalkan"
              value={dibatalkan}
              sub="CANCEL dalam periode"
              icon={XCircle}
              accent="#991B1B"
              testid="metric-dibatalkan"
              onClick={() => navigate(withStatus("CANCEL"))}
            />
          </div>
        );
      })()}

      {(user?.role === "admin" || user?.role === "koordinator") && (
        <TechnicianStats />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dashboard-distribution-row">
        <div className="bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] min-w-0 flex flex-col" data-testid="wilayah-card">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
            <h3 className="font-display text-sm sm:text-base font-bold tracking-tight flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#0055FF]" /> Distribusi Tiket per Wilayah
              <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Top 5</span>
            </h3>
            <p className="text-[11px] text-[#9CA3AF]" data-testid="wilayah-chart-hint">
              Klik bar untuk melihat tiket wilayah tersebut
            </p>
          </div>
          <WilayahChart data={stats?.by_wilayah || []} limit={5} />
          {(stats?.by_wilayah || []).length > 0 && (
            <button
              type="button"
              onClick={() => setWilayahAllOpen(true)}
              className="group mt-auto self-start inline-flex items-center gap-1.5 text-xs font-bold text-[#0055FF] hover:text-[#0041C4] px-2 py-1.5 -ml-2 rounded-md hover:bg-[#0055FF]/5 transition-colors"
              data-testid="wilayah-view-all"
            >
              Lihat Semua Wilayah ({(stats?.by_wilayah || []).length})
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>
          )}
          <WilayahAllDialog
            open={wilayahAllOpen}
            onOpenChange={setWilayahAllOpen}
            data={stats?.by_wilayah || []}
          />
        </div>

        {/* Distribusi Jenis Pekerjaan — donut berwarna, angka aktual.
            Slice/legend dengan jenis valid dapat diklik → /tickets?jenis_pekerjaan=…
            "-" (tanpa jenis) tidak dapat diklik karena backend tak punya filter kosong. */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl px-4 py-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] min-w-0" data-testid="jenis-pekerjaan-card">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
            <h3 className="font-display text-sm sm:text-base font-bold tracking-tight flex items-center gap-2">
              <Wrench className="h-4 w-4 text-[#6D28D9]" /> Distribusi Jenis Pekerjaan
            </h3>
            <p className="text-[11px] text-[#9CA3AF]">Klik untuk melihat tiket jenis tersebut</p>
          </div>
          <JenisPekerjaanDonut
            rows={stats?.by_jenis_pekerjaan || []}
            onDrill={(key) => {
              const p = new URLSearchParams();
              if (dateRange.date_from) p.set("date_from", dateRange.date_from);
              if (dateRange.date_to) p.set("date_to", dateRange.date_to);
              p.set("jenis_pekerjaan", key);
              navigate(`/tickets?${p.toString()}`);
            }}
          />
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-[0_1px_2px_rgba(16,24,40,0.04)] overflow-hidden" data-testid="recent-tickets-card">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF]">Aktivitas Terbaru</div>
            <h3 className="font-display text-base sm:text-lg font-bold tracking-tight mt-0.5 flex items-center gap-2">
              <TicketIcon className="h-4 w-4 text-[#0055FF]" /> Tiket Terakhir
            </h3>
          </div>
          <button
            type="button"
            onClick={() => {
              const p = new URLSearchParams();
              if (dateRange.date_from) p.set("date_from", dateRange.date_from);
              if (dateRange.date_to) p.set("date_to", dateRange.date_to);
              const qs = p.toString();
              navigate(`/tickets${qs ? "?" + qs : ""}`);
            }}
            className="group inline-flex items-center gap-1.5 text-xs font-bold text-[#0055FF] hover:text-[#0041C4] px-2 py-1.5 rounded-md hover:bg-[#0055FF]/5 transition-colors"
            data-testid="recent-view-all"
          >
            Lihat Semua <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
        {/* Desktop: table with clickable rows (same behavior as the mobile card) */}
        <div className="hidden md:block overflow-x-auto" data-testid="recent-tickets-table">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
              <tr>
                <th className="px-6 py-3">No. Tiket</th>
                <th className="px-6 py-3">Lokasi</th>
                <th className="px-6 py-3">Wilayah</th>
                <th className="px-6 py-3">PIC</th>
                <th className="px-6 py-3">Jenis</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3 whitespace-nowrap">Dibuat</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => {
                const c = themeForStatus(r.status, statusMaster);
                const openDetail = () => navigate(`/tickets?open=${encodeURIComponent(r.id)}`);
                const onKey = (e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(); }
                };
                return (
                  <tr
                    key={r.id}
                    role="button"
                    tabIndex={0}
                    onClick={openDetail}
                    onKeyDown={onKey}
                    data-testid={`recent-row-${r.id}`}
                    aria-label={`Buka detail tiket ${r.no_tiket || r.id}`}
                    className="group border-t border-[#E5E7EB] cursor-pointer hover:bg-[rgba(0,85,255,0.08)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0055FF]"
                  >
                    <td className="px-6 py-3 font-mono-tab font-semibold text-[#0055FF] transition-colors group-hover:text-[#0041C4] group-hover:underline">{r.no_tiket || "-"}</td>
                    <td className="px-6 py-3 max-w-xs truncate">{r.objek_lokasi || "-"}</td>
                    <td className="px-6 py-3">{r.wilayah || "-"}</td>
                    <td className="px-6 py-3">{r.pic || "-"}</td>
                    <td className="px-6 py-3">{r.jenis_pekerjaan || "-"}</td>
                    <td className="px-6 py-3">
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border group-hover:underline"
                        style={{ background: c.bg, color: c.fg, borderColor: c.border }}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-xs text-[#4B5563] font-mono-tab" data-testid={`recent-created-${r.id}`}>
                      {createdLabel(r)}
                    </td>
                  </tr>
                );
              })}
              {recent.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-[#9CA3AF]">
                    Belum ada tiket. Silakan buat tiket baru di halaman Manajemen Pekerjaan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: ticket card list */}
        <ul className="md:hidden divide-y divide-[#E5E7EB]" data-testid="recent-tickets-cards">
          {recent.length === 0 && (
            <li className="px-6 py-10 text-center text-sm text-[#9CA3AF]">
              Belum ada tiket. Silakan buat tiket baru di halaman Manajemen Pekerjaan.
            </li>
          )}
          {recent.map((r) => {
            const c = themeForStatus(r.status, statusMaster);
            const openDetail = () => navigate(`/tickets?open=${encodeURIComponent(r.id)}`);
            const onKey = (e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openDetail(); }
            };
            return (
              <li
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={openDetail}
                onKeyDown={onKey}
                data-testid={`recent-card-${r.id}`}
                aria-label={`Buka detail tiket ${r.no_tiket || r.id}`}
                className="px-4 py-3 cursor-pointer transition-all duration-100 hover:bg-[rgba(0,85,255,0.08)] active:scale-[0.985] active:bg-[rgba(0,85,255,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-tab font-semibold text-[13px] text-[#0A0A0A] truncate">
                    {r.no_tiket || "-"}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border flex-shrink-0"
                    style={{ background: c.bg, color: c.fg, borderColor: c.border }}
                  >
                    {r.status}
                  </span>
                </div>
                <div className="mt-1.5 text-sm text-[#0A0A0A] font-medium line-clamp-2 break-words">
                  {r.objek_lokasi || "-"}
                </div>
                <div className="mt-0.5 text-[10px] text-[#9CA3AF] font-mono-tab" data-testid={`recent-card-created-${r.id}`}>
                  Dibuat {createdLabel(r)}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#4B5563]">
                  {r.wilayah && <span>{r.wilayah}</span>}
                  {r.pic && <span className="text-[#9CA3AF]">·</span>}
                  {r.pic && <span className="truncate max-w-[60%]">{r.pic}</span>}
                  {r.jenis_pekerjaan && <span className="text-[#9CA3AF]">·</span>}
                  {r.jenis_pekerjaan && (
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563] bg-[#F3F4F6] px-1.5 py-0.5 rounded">
                      {r.jenis_pekerjaan}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}


// Kolom "Dibuat": created_at (timestamp, WIB) → fallback tgl_masuk (tanggal saja).
function createdLabel(r) {
  if (r?.created_at) return formatWIB(r.created_at);
  if (r?.tgl_masuk) return formatWIBDate(r.tgl_masuk);
  return "-";
}

const JENIS_COLORS = { PSB: "#2563EB", REP: "#EA580C", NET: "#16A34A" };
const JENIS_OTHER = ["#7C3AED", "#A855F7", "#C026D3", "#6D28D9", "#8B5CF6", "#DB2777"];
function jenisColor(key, idx) {
  if (key === "-") return "#9CA3AF";
  return JENIS_COLORS[key] || JENIS_OTHER[idx % JENIS_OTHER.length];
}

function JenisPekerjaanDonut({ rows, onDrill }) {
  if (rows.length === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-xs text-[#9CA3AF]" data-testid="jenis-pekerjaan-empty">
        Tidak ada data pada rentang ini.
      </div>
    );
  }
  let other = 0;
  const data = rows.map((r) => {
    const key = r.jenis_pekerjaan || "-";
    const known = key === "-" || JENIS_COLORS[key];
    const color = jenisColor(key, known ? 0 : other++);
    return { key, count: r.count || 0, color, clickable: key !== "-" };
  });
  const total = data.reduce((n, d) => n + d.count, 0);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3 sm:gap-5" data-testid="jenis-pekerjaan-donut">
      <div className="relative h-[170px] w-full max-w-[200px] mx-auto [&_.recharts-sector:focus]:outline-none [&_.recharts-surface:focus]:outline-none [&_.recharts-wrapper:focus]:outline-none">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="key" innerRadius={52} outerRadius={80}
                 paddingAngle={data.length > 1 ? 2 : 0} stroke="#fff" strokeWidth={2} isAnimationActive={false}>
              {data.map((d) => (
                <Cell key={d.key} fill={d.color} cursor={d.clickable ? "pointer" : "default"}
                      onClick={d.clickable ? () => onDrill(d.key) : undefined}
                      data-testid={`jenis-pekerjaan-slice-${d.key}`} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 6, fontSize: 12 }}
                     formatter={(v, n) => [`${v} tiket`, n]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="font-display text-2xl font-black tracking-tighter font-mono-tab" data-testid="jenis-pekerjaan-total">{total}</span>
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">Tiket</span>
        </div>
      </div>
      <div className="space-y-1 max-h-[180px] overflow-y-auto pr-1" data-testid="jenis-pekerjaan-list">
        {data.map((d) => {
          const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : "0.0";
          const onKey = (e) => {
            if (!d.clickable) return;
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onDrill(d.key); }
          };
          return (
            <div
              key={d.key}
              role={d.clickable ? "button" : undefined}
              tabIndex={d.clickable ? 0 : undefined}
              onClick={d.clickable ? () => onDrill(d.key) : undefined}
              onKeyDown={d.clickable ? onKey : undefined}
              aria-label={d.clickable ? `Lihat tiket jenis ${d.key}` : undefined}
              data-testid={`jenis-pekerjaan-row-${d.key}`}
              className={`group grid grid-cols-[12px_1fr_auto_48px] items-center gap-2 rounded-md px-1.5 py-1 transition-colors ${d.clickable ? "cursor-pointer hover:bg-[#F3F4F6] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6D28D9]" : ""}`}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
              <span className={`text-xs font-bold uppercase tracking-wider truncate ${d.clickable ? "text-[#111827] group-hover:underline" : "text-[#9CA3AF]"}`}>
                {d.key}
              </span>
              <span className="font-mono-tab font-black text-sm text-right" data-testid={`jenis-pekerjaan-count-${d.key}`}>
                {d.count}
              </span>
              <span className="text-[11px] text-[#6B7280] text-right font-mono-tab">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function wilayahDrilldown(navigate, wilayah) {
  if (!wilayah) return;
  const from = computeThisMonthRange();
  const qs = new URLSearchParams({ wilayah, date_from: from.date_from, date_to: from.date_to });
  navigate(`/tickets?${qs.toString()}`);
}

function WilayahAllDialog({ open, onOpenChange, data }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const rows = useMemo(() => [...(data || [])].sort((a, b) => (b.count || 0) - (a.count || 0)), [data]);
  const max = rows.reduce((m, r) => Math.max(m, r.count || 0), 0) || 1;
  const total = rows.reduce((n, r) => n + (r.count || 0), 0);
  const term = q.trim().toLowerCase();
  const shown = term ? rows.filter((r) => String(r.wilayah || "").toLowerCase().includes(term)) : rows;
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setQ(""); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] p-0 gap-0" data-testid="wilayah-all-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E5E7EB] text-left">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4 text-[#0055FF]" /> Semua Wilayah
          </DialogTitle>
          <DialogDescription className="text-xs" data-testid="wilayah-all-summary">
            {rows.length} wilayah · {total} tiket · klik bar untuk melihat tiket
          </DialogDescription>
          <div className="relative mt-2">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama wilayah…"
                   className="w-full h-9 pl-8 pr-2 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
                   data-testid="wilayah-all-search" />
          </div>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-3 space-y-1.5" data-testid="wilayah-all-list">
          {shown.map((r) => (
            <button
              key={r.wilayah}
              type="button"
              onClick={() => { onOpenChange(false); wilayahDrilldown(navigate, r.wilayah); }}
              className="group w-full grid grid-cols-[minmax(0,140px)_1fr_40px] items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-[rgba(0,85,255,0.06)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
              data-testid={`wilayah-all-row-${r.wilayah}`}
            >
              <span className="text-xs text-[#111827] truncate group-hover:text-[#0055FF] group-hover:font-semibold">{r.wilayah}</span>
              <span className="h-4 rounded bg-[#F3F4F6] overflow-hidden">
                <span className="block h-full rounded bg-[#0055FF]" style={{ width: `${Math.max((r.count || 0) > 0 ? 2 : 0, ((r.count || 0) / max) * 100)}%` }} />
              </span>
              <span className="font-mono-tab text-xs font-bold text-right" data-testid={`wilayah-all-count-${r.wilayah}`}>{r.count}</span>
            </button>
          ))}
          {shown.length === 0 && (
            <div className="py-8 text-center text-xs text-[#9CA3AF]" data-testid="wilayah-all-empty">Tidak ada wilayah yang cocok.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// "Tiket per Wilayah" — unified horizontal bar chart for every viewport.
// Rationale: as the number of wilayah grows, a vertical x-axis produces
// unreadable, overlapping labels. A horizontal layout keeps every wilayah
// name fully visible on the left regardless of count.
// ---------------------------------------------------------------------------
function useIsMobile(query = "(max-width: 1023px)") {
  const get = () => (typeof window !== "undefined"
    ? window.matchMedia(query).matches : false);
  const [is, setIs] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const on = () => setIs(mq.matches);
    mq.addEventListener?.("change", on) ?? mq.addListener?.(on);
    return () => (mq.removeEventListener?.("change", on) ?? mq.removeListener?.(on));
  }, [query]);
  return is;
}

function WilayahChart({ data, limit }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  // Row hover state — shared between YAxis tick label and Bar so hovering
  // either surface highlights the whole row (label bg + text color boost).
  // This signals to the user that label + bar are the SAME tap zone.
  // Declared at top of component to satisfy React hooks rules (must come
  // before any conditional return).
  const [hoveredWilayah, setHoveredWilayah] = useState(null);
  // Sort descending by count — busiest wilayah always on top.
  const rows = useMemo(() => {
    const sorted = [...(data || [])].sort((a, b) => (b.count || 0) - (a.count || 0));
    return limit ? sorted.slice(0, limit) : sorted;
  }, [data, limit]);
  const n = rows.length;

  if (n === 0) {
    return (
      <div className="h-32 flex items-center justify-center text-sm text-[#9CA3AF]"
           data-testid="wilayah-chart-empty">
        Belum ada data wilayah.
      </div>
    );
  }

  // Dynamic vertical size: height = n × 36 + padding, capped ~420px with
  // internal scroll so a growing wilayah list never elongates the page.
  const ROW_PX = 28;
  const chartHeight = Math.max(160, n * ROW_PX + 24);
  // Y-axis label width — desktop has more room for the full name so we allow
  // more px than on mobile where card width is scarce.
  const yAxisWidth = isMobile ? 120 : 160;

  const drilldown = (payload) => wilayahDrilldown(navigate, payload?.wilayah);

  return (
    <div
      className="max-h-[564px] overflow-y-auto overflow-x-hidden pr-1"
      data-testid={isMobile ? "wilayah-chart-mobile" : "wilayah-chart-desktop"}
    >
      <div style={{ height: chartHeight, width: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={rows}
            margin={{ top: 4, right: 32, left: 4, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#4B5563" }} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="wilayah"
              width={yAxisWidth}
              interval={0}
              tick={(props) => {
                // Custom clickable tick — makes Y-axis label sebagai
                // secondary tap zone (selain bar). Cursor pointer + onClick
                // memicu drilldown yang sama seperti klik bar.
                // Hover: subtle bg rect + bold text supaya user tahu label
                // + bar bagian tap zone yang sama (state shared via
                // hoveredWilayah).
                const { x, y, payload } = props;
                const wilayah = payload?.value;
                const isHover = hoveredWilayah === wilayah;
                return (
                  <g
                    transform={`translate(${x},${y})`}
                    onClick={() => drilldown({ wilayah })}
                    onMouseEnter={() => setHoveredWilayah(wilayah)}
                    onMouseLeave={() => setHoveredWilayah(null)}
                    style={{ cursor: "pointer" }}
                    data-testid="wilayah-y-tick"
                  >
                    <rect
                      x={-yAxisWidth}
                      y={-12}
                      width={yAxisWidth - 2}
                      height={24}
                      rx={3}
                      fill={isHover ? "rgba(0,85,255,0.08)" : "transparent"}
                    />
                    <text
                      x={-4}
                      y={0}
                      dy={4}
                      textAnchor="end"
                      fontSize={11}
                      fill={isHover ? "#0055FF" : "#111827"}
                      fontWeight={isHover ? 600 : 400}
                    >
                      {wilayah}
                    </text>
                  </g>
                );
              }}
            />
            <Tooltip
              contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 6, fontSize: 12 }}
              cursor={{ fill: "rgba(0,85,255,0.06)" }}
              formatter={(value) => [`${value} tiket`, "Jumlah"]}
            />
            <Bar
              dataKey="count"
              fill="#0055FF"
              radius={[0, 4, 4, 0]}
              cursor="pointer"
              onClick={(entry) => drilldown(entry && entry.payload)}
              onMouseEnter={(entry) => setHoveredWilayah(entry?.payload?.wilayah || null)}
              onMouseLeave={() => setHoveredWilayah(null)}
              label={{ position: "right", fill: "#4B5563", fontSize: 11 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

